import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileWarning } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { NotebookDocList } from "@/components/notebook/NotebookDocList";
import { NotebookDocView } from "@/components/notebook/NotebookDocView";
import { NotebookEmptyState } from "@/components/notebook/NotebookEmptyState";
import { ImportMarkdownModal } from "@/components/notebook/ImportMarkdownModal";
import { type Note, NotebookError, useNotebook } from "@/hooks/useNotebook";
import { useNotebookFolders } from "@/hooks/useNotebookFolders";
import { cn } from "@/lib/utils";
import { useNotebookLayoutStore } from "@/store/notebookLayoutStore";
import { isWithinFolder, parentFolderOf } from "@domain/notebook-folders";
import { deriveNoteFileName, validateNotebookPath } from "@domain/notebook-path";
import { notebookInvalidPath, notebookReadFailed } from "@domain/user-errors";

export const Route = createFileRoute("/_authenticated/notebook")({
  component: NotebookRoute,
  validateSearch: (search: Record<string, unknown>): { path?: string } => {
    // `?path=` deep-links ONE note: the chat's "New note created" card and the
    // notebook prop window both promote to the full page with the exact note
    // already selected. The value is the VAULT-RELATIVE path (`about-you.md`,
    // `projects/idea.md`) — the same identity `selectedPath` and the list rows
    // carry, so it round-trips without a translation layer.
    //
    // Narrowed through the notebook domain's REJECT-only validator, so a
    // traversal-shaped or non-markdown value never reaches selection state; it
    // is simply OMITTED (not `undefined`), leaving existing navigations to this
    // route free to pass no `search` at all.
    if (typeof search.path !== "string") return {};
    return validateNotebookPath({ path: search.path }).isErr()
      ? {}
      : { path: search.path };
  },
});

/** Note title = filename (last path segment minus `.md`) — identity is the path. */
function titleFromPathString(path: string): string {
  const last = path.split("/").pop() ?? path;
  return last.replace(/\.md$/i, "") || path;
}

/** A draft path for a brand-new, not-yet-saved note. Kept out of the fetched
 *  list until its first save lands. */
const DRAFT_PATH = "__draft__.md";

function NotebookRoute() {
  const navigate = useNavigate();
  const { path: deepLinkPath } = Route.useSearch();
  const notebook = useNotebook();
  const { notes, isLoading, error, lastRefreshedAt, refresh, readNote, saveNote, deleteNote } =
    notebook;
  const folderOps = useNotebookFolders(notebook);
  const listHiddenPref = useNotebookLayoutStore((s) => s.listHidden);
  const toggleListHidden = useNotebookLayoutStore((s) => s.toggleListHidden);

  // Selection is a PATH — never a `.find()` render-gate (§4.3). Seeded from
  // the `?path=` deep link in the INITIALIZER, not an effect, so a direct URL
  // load never flashes the "Select a note" / empty-state pane first.
  const [selectedPath, setSelectedPath] = useState<string | null>(
    () => deepLinkPath ?? null,
  );
  const [query, setQuery] = useState("");
  const [editOnOpen, setEditOnOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  /** Destination folder for a draft note ("" = root) — set by "New note here". */
  const [draftFolder, setDraftFolder] = useState("");

  // Per-note body fetch — the list is metadata-only, so the detail pane owns
  // reading the selected note's content (with its own loading / not-found).
  const [note, setNote] = useState<Note | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  /** Read failure that is NOT not-found (transport / oversize) — display copy. */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Save/delete failure display copy — shown inline in the detail pane. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /**
   * Path whose body we ALREADY hold because a save just returned it. A draft's
   * first save moves BOTH of the read effect's deps at once (`isDraft` false +
   * `selectedPath` draft→real), so without this the effect re-fires, wipes the
   * just-saved `note` to null, and re-reads a body we never lost. The
   * `key={selectedPath}` remount then rebuilds the editor from that null —
   * blank until a reload. Not a cache (§4.13 holds no cache beyond the current
   * snapshot): the write's own result IS this snapshot.
   */
  const hydratedPathRef = useRef<string | null>(null);
  /**
   * Where the OPEN note's body is currently persisted — the autosave target.
   *
   * For a saved note this is just its path. For a draft it is `null` until the
   * first autosave materializes it, and thereafter the real path the body lives
   * at while `selectedPath` is STILL the `__draft__.md` sentinel. Keeping the
   * selection on the sentinel is the point: materializing by moving
   * `selectedPath` would change the detail pane's `key={selectedPath}` and
   * remount the editor mid-keystroke. The selection catches up at commit, when
   * `handleSave` renames `untitled.md` → the titled path and deletes the old.
   */
  const savedPathRef = useRef<string | null>(null);

  // Deep-link consume-then-strip (§4.11 discipline, same shape as the chat
  // route's one-shot `?seed=`). The initializer above already applied the param
  // on a cold load; this covers a navigation that lands on an ALREADY-mounted
  // route (the initializer would not re-run), then drops the param so a later
  // in-page selection can never be contradicted by a stale URL.
  useEffect(() => {
    if (deepLinkPath === undefined) return;
    setIsDraft(false);
    setEditOnOpen(false);
    setSelectedPath(deepLinkPath);
    void navigate({ to: "/notebook", search: {}, replace: true });
  }, [deepLinkPath, navigate]);

  useEffect(() => {
    setActionError(null);
    if (selectedPath === null || isDraft) {
      setNote(null);
      setNotFound(false);
      setLoadError(null);
      setNoteLoading(false);
      return;
    }
    // Post-save hydration: keep the body the save already gave us. One-shot —
    // cleared on use, so any later selection of this path reads normally.
    if (hydratedPathRef.current === selectedPath) {
      hydratedPathRef.current = null;
      setNotFound(false);
      setLoadError(null);
      setNoteLoading(false);
      return;
    }
    let cancelled = false;
    setNoteLoading(true);
    setNotFound(false);
    setLoadError(null);
    setNote(null);
    readNote({ path: selectedPath })
      .then((n) => {
        if (!cancelled) setNote(n);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Branch on the CODE, never the display copy (§5 error table): only a
        // true NOT_FOUND renders the not-found pane; everything else shows its
        // own error copy so a transport failure never masquerades as deletion.
        if (err instanceof NotebookError && err.code === "NOT_FOUND") {
          setNotFound(true);
        } else {
          setLoadError(err instanceof NotebookError ? err.userMessage : notebookReadFailed());
        }
      })
      .finally(() => {
        if (!cancelled) setNoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPath, isDraft, readNote]);

  // Title/path-only search over the metadata-only list (§4.3).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter(
      (n) =>
        n.path.toLowerCase().includes(q) ||
        titleFromPathString(n.path).toLowerCase().includes(q),
    );
  }, [notes, query]);

  function handleNewNote(folder = "") {
    // A fresh draft has nothing on disk yet — never inherit the previous one's
    // materialized path, or this note's first autosave would overwrite it.
    savedPathRef.current = null;
    setIsDraft(true);
    setDraftFolder(folder);
    setNote({ path: DRAFT_PATH, content: "", frontmatter: null });
    setEditOnOpen(true);
    setSelectedPath(DRAFT_PATH);
  }

  /** Drag/picker note move — selection follows the note to its new path. */
  async function handleMoveNote({ path, folder }: { path: string; folder: string }) {
    const newPath = await folderOps.moveNote({ path, folder });
    if (newPath !== null && selectedPath === path) setSelectedPath(newPath);
  }

  async function handleRenameFolder({ path, newPath }: { path: string; newPath: string }) {
    await folderOps.renameFolder({ path, newPath });
    // The selected note may have moved with its folder — re-point, don't 404.
    if (selectedPath && isWithinFolder(parentFolderOf(selectedPath), path)) {
      setSelectedPath(`${newPath}${selectedPath.slice(path.length)}`);
    }
  }

  async function handleDeleteFolder(path: string) {
    await folderOps.deleteFolder(path);
    // Flattened notes may carry collision suffixes — deselect rather than guess.
    if (selectedPath && isWithinFolder(parentFolderOf(selectedPath), path)) {
      setSelectedPath(null);
      setNote(null);
    }
  }

  // Empty-state chip → hand the prompt to the agent in /chat (no local seeding).
  function handleAskAgent(prompt: string) {
    void navigate({ to: "/chat", search: { template: prompt } });
  }

  /**
   * BODY-ONLY autosave (§4.13). Writes to the path the note already lives at
   * and stops there: no path derivation, no rename, no delete, no selection
   * move, no `refresh()`.
   *
   * Every one of those omissions is load-bearing. Deriving a path from the
   * title on a typing pause would rename per pause — `m.md` → `my.md` (delete
   * `m.md`) → `my-n.md` (delete `my.md`) — and each rename moves `selectedPath`,
   * which remounts the editor through `key={selectedPath}` while the user is
   * still typing. And `refresh()` is a full `listNotes` round-trip; firing it
   * per pause is exactly the read amplification §4.13 exists to prevent. The
   * list's `updatedAt`/`sizeBytes` therefore lag until the next real refresh —
   * the sanctioned visible-staleness trade.
   *
   * A draft materializes here ONCE, under its current title or `Untitled`, and
   * the real path is parked in `savedPathRef` rather than in `selectedPath`.
   */
  async function handleAutosave({
    title,
    content,
  }: {
    title: string;
    content: string;
  }): Promise<boolean> {
    // Consulted ONLY while drafting — otherwise a ref left over from a previous
    // draft could redirect a real note's autosave at the wrong file.
    let target = isDraft ? savedPathRef.current : selectedPath;
    if (target === null) {
      const derived = deriveNoteFileName({ title: title.trim() || "Untitled" });
      if (derived.isErr()) {
        setActionError(notebookInvalidPath());
        return false;
      }
      target = draftFolder ? `${draftFolder}/${derived.value}` : derived.value;
    }

    setActionError(null);
    try {
      await saveNote({ path: target, content });
      savedPathRef.current = target;
      return true;
    } catch (err) {
      setActionError(err instanceof NotebookError ? err.userMessage : String(err));
      return false;
    }
  }

  /** Returns true on success — DocView leaves edit mode only then. */
  async function handleSave({
    title,
    content,
  }: {
    title: string;
    content: string;
  }): Promise<boolean> {
    // A materialized draft already has a real file on disk; that file — not the
    // `__draft__.md` sentinel — is what a rename must move and clean up.
    const oldPath = isDraft ? savedPathRef.current : selectedPath;

    // Path derivation: an UNCHANGED title never re-derives the path — the note
    // (nested or not) stays exactly where it is. A changed title renames only
    // the FILENAME; the directory prefix is preserved (rename ≠ relocate).
    let newPath: string;
    if (oldPath !== null && title.trim() === titleFromPathString(oldPath)) {
      newPath = oldPath;
    } else {
      const derived = deriveNoteFileName({ title });
      if (derived.isErr()) {
        setActionError(notebookInvalidPath());
        return false;
      }
      const dirEnd = oldPath?.lastIndexOf("/") ?? -1;
      // Drafts land in the folder "New note here" targeted; existing notes
      // keep their directory (rename ≠ relocate).
      const dir =
        oldPath !== null ? (dirEnd > 0 ? oldPath.slice(0, dirEnd) : "") : draftFolder;
      newPath = dir ? `${dir}/${derived.value}` : derived.value;
    }

    setIsSaving(true);
    setActionError(null);
    try {
      // `fromPath` carries provenance across a rename (server merge-preserve).
      await saveNote({ path: newPath, content, fromPath: oldPath ?? undefined });
      // Rename-as-move: write new path THEN delete old (crash leaves both — no
      // data loss, §4.3). A no-op when the path is unchanged.
      if (oldPath && oldPath !== newPath) {
        await deleteNote({ path: oldPath });
      }
      // Claim the read BEFORE the selection moves — the effect that would
      // otherwise discard this body runs on the very next commit.
      hydratedPathRef.current = newPath;
      setIsDraft(false);
      setSelectedPath(newPath);
      setNote({ path: newPath, content, frontmatter: note?.frontmatter ?? null });
      await refresh();
      return true;
    } catch (err) {
      setActionError(err instanceof NotebookError ? err.userMessage : String(err));
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (selectedPath === null) return;
    if (!isDraft) {
      try {
        await deleteNote({ path: selectedPath });
        await refresh();
      } catch (err) {
        // Keep the selection so the user can retry; show why it failed.
        setActionError(err instanceof NotebookError ? err.userMessage : String(err));
        return;
      }
    }
    setIsDraft(false);
    setSelectedPath(null);
    setNote(null);
  }

  const hasSelection = selectedPath !== null;

  // Full-screen reading: the stored flag is only a PREFERENCE. Applying it with
  // nothing selected would render neither the list nor a note — an empty pane
  // with no affordance to bring either back — so the gate is not optional.
  const listHidden = listHiddenPref && hasSelection;

  // List-load failure with nothing to show: a full vault must NEVER be
  // indistinguishable from "No notes yet" — render the failure, offer retry.
  if (!isLoading && error && notes.length === 0 && !hasSelection) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col items-center justify-center gap-3 px-4 text-center">
        <FileWarning className="h-8 w-8 text-black/30 dark:text-white/30" strokeWidth={1.8} />
        {/* Both lines share ONE measure. `error` is interpolated copy that
            carries the support-email suffix (~90 chars), so an unconstrained
            <p> ran the full viewport width on desktop while the static
            subtext below sat at max-w-xs — two wildly mismatched columns. */}
        <div className="flex max-w-md flex-col gap-2">
          <p className="text-sm font-bold text-black/70 dark:text-white/70">{error}</p>
          <p className="text-xs text-black/45 dark:text-white/45">
            Your notes are safe — this screen just couldn&apos;t load them.
          </p>
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded-full bg-home-yellow px-5 py-2 text-sm font-bold text-home-ink transition-transform active:scale-95"
        >
          Try again
        </button>
      </div>
    );
  }

  // First run — no notes and nothing selected: the promise screen owns the page.
  if (!isLoading && !error && notes.length === 0 && !hasSelection) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center overflow-y-auto px-4">
        <NotebookEmptyState
          onWriteNote={handleNewNote}
          onAskAgent={handleAskAgent}
          onImport={() => setImportOpen(true)}
        />
        <ImportMarkdownModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          folders={folderOps.folders}
          onImport={(files, folder) => folderOps.importNotes({ files, folder })}
        />
      </div>
    );
  }

  const viewTitle = selectedPath ? titleFromPathString(selectedPath) : "";

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col overflow-hidden">
      {/* Refresh failed but a (stale) list is showing — say so, don't pretend. */}
      {error && notes.length > 0 && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-red-200 bg-red-50 px-4 py-2 dark:border-red-900/40 dark:bg-red-950/30">
          <span className="text-xs font-bold text-red-700 dark:text-red-300">
            {error} Showing the last loaded list.
          </span>
          <button
            onClick={() => void refresh()}
            className="shrink-0 text-xs font-bold text-red-700 underline-offset-2 hover:underline dark:text-red-300"
          >
            Retry
          </button>
        </div>
      )}
      {/* Folder-op failure (partial or rejected name) — honest, retryable. */}
      {folderOps.error && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2 text-xs font-bold text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {folderOps.error}
        </div>
      )}
      <div className="flex min-h-0 flex-1 overflow-hidden">
      <NotebookDocList
        notes={filtered}
        selectedPath={selectedPath}
        query={query}
        onQueryChange={setQuery}
        onSelect={(path) => {
          setEditOnOpen(false);
          setIsDraft(false);
          setSelectedPath(path);
        }}
        onNewNote={handleNewNote}
        onRefresh={() => void refresh()}
        isLoading={isLoading}
        lastRefreshedAt={lastRefreshedAt}
        folders={folderOps.folders}
        onMoveNote={(args) => void handleMoveNote(args)}
        onCreateFolder={folderOps.createFolder}
        onRenameFolder={(args) => void handleRenameFolder(args)}
        onDeleteFolder={(path) => void handleDeleteFolder(path)}
        onImport={() => setImportOpen(true)}
        className={cn(
          // Mobile: the list yields to the note it opened. Desktop: both panes
          // sit side by side unless full-screen reading is on.
          hasSelection ? "hidden md:flex" : "flex",
          listHidden && "md:hidden",
        )}
      />
      <ImportMarkdownModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        folders={folderOps.folders}
        onImport={(files, folder) => folderOps.importNotes({ files, folder })}
      />
      {hasSelection ? (
        <NotebookDocView
          key={selectedPath}
          path={isDraft ? "" : (selectedPath as string)}
          note={note}
          updatedAt={
            isDraft
              ? null
              : (notes.find((n) => n.path === selectedPath)?.updatedAt ?? null)
          }
          isLoading={noteLoading}
          notFound={notFound}
          loadError={loadError}
          actionError={actionError}
          title={isDraft ? "" : viewTitle}
          startEditing={editOnOpen}
          isSaving={isSaving}
          onSave={handleSave}
          onAutosave={handleAutosave}
          isListHidden={listHidden}
          onToggleList={toggleListHidden}
          onDelete={() => void handleDelete()}
          folders={folderOps.folders}
          onMove={
            isDraft
              ? undefined
              : (folder) => void handleMoveNote({ path: selectedPath as string, folder })
          }
          onBack={() => {
            setSelectedPath(null);
            setIsDraft(false);
            setNote(null);
          }}
        />
      ) : (
        <div className="hidden flex-1 items-center justify-center text-black/40 dark:text-white/40 md:flex">
          Select a note to read
        </div>
      )}
      </div>
    </div>
  );
}
